import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, ChevronRight, ChevronLeft, Loader2, MessageCircle } from "lucide-react";
import { api } from "../../lib/api";
import { calculateQuote, formatGBP, NIGERIAN_STATES, FIXED_PRICE_KEYS } from "../../lib/pricing";
import { buildWhatsAppLink } from "../../lib/whatsapp";

const STEPS = ["Your Order", "Delivery Details", "Review & Submit"];

const inputCls =
  "w-full px-4 py-3 bg-[#F8F5F0] border border-[#E5E0D8] text-[#1A1A1A] text-base focus:outline-none focus:border-[#C9A96E] transition-colors";

export default function OrderForm() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    product_url: "",
    product_description: "",
    product_value_gbp: "",
    weight_kg: "",
    complex_sourcing: false,
    fixed_price_item: "",
    special_instructions: "",
    client_name: "",
    whatsapp: "+234",
    email: "",
    destination_state: "Lagos",
    city: "",
    address: "",
  });

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const quote = useMemo(
    () =>
      calculateQuote({
        productValue: parseFloat(form.product_value_gbp) || 0,
        weightKg: parseFloat(form.weight_kg) || 0,
        state: form.destination_state,
        isComplexSourcing: form.complex_sourcing,
        fixedPriceItem: form.fixed_price_item || null,
      }),
    [form],
  );

  const validateStep = () => {
    setError(null);
    if (step === 0) {
      if (!form.product_url && !form.product_description) {
        setError("Provide a product URL or a description.");
        return false;
      }
      const pv = parseFloat(form.product_value_gbp);
      if (!pv || pv <= 0) {
        setError("Enter an estimated product value in £.");
        return false;
      }
    }
    if (step === 1) {
      if (!form.client_name.trim()) return setError("Enter your full name."), false;
      if (!form.email.trim() || !form.email.includes("@")) return setError("Enter a valid email."), false;
      if (!form.whatsapp.trim() || form.whatsapp.length < 7) return setError("Enter your WhatsApp number."), false;
      if (!form.city.trim()) return setError("Enter your city."), false;
      if (!form.address.trim()) return setError("Enter your delivery address."), false;
    }
    return true;
  };

  const next = () => validateStep() && setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const buildOrderWhatsAppMessage = (ref, f, q) => {
    const lines = [
      `Hi MPS! I've just placed a new order.`,
      ``,
      `Reference: ${ref}`,
      ``,
      `CLIENT`,
      `Name: ${f.client_name}`,
      `Email: ${f.email}`,
      `WhatsApp: ${f.whatsapp}`,
      ``,
      `PRODUCT`,
      f.product_url ? `Link: ${f.product_url}` : null,
      f.product_description ? `Description: ${f.product_description}` : null,
      f.fixed_price_item ? `Fixed-price item: ${f.fixed_price_item}` : null,
      `Value: £${(parseFloat(f.product_value_gbp) || 0).toFixed(2)}`,
      f.weight_kg ? `Weight: ${f.weight_kg}kg` : null,
      `Sourcing add-on: ${f.complex_sourcing ? "Yes (+£30)" : "No"}`,
      ``,
      `DELIVERY`,
      `State: ${f.destination_state}`,
      `City: ${f.city}`,
      `Address: ${f.address}`,
      ``,
      `ESTIMATE`,
      `Service Fee: £${q.serviceFee.toFixed(2)}`,
      `Freight: £${q.freightCost.toFixed(2)}`,
      `Sourcing Fee: £${q.sourcingFee.toFixed(2)}`,
      `Estimated Total: £${q.total.toFixed(2)}`,
      f.special_instructions ? `` : null,
      f.special_instructions ? `NOTES` : null,
      f.special_instructions ? f.special_instructions : null,
    ].filter(Boolean);
    return lines.join("\n");
  };

  const submit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        product_url: form.product_url || null,
        product_description: form.product_description || null,
        product_value_gbp: parseFloat(form.product_value_gbp) || 0,
        complex_sourcing: !!form.complex_sourcing,
        special_instructions: form.special_instructions || null,
        fixed_price_item: form.fixed_price_item || null,
        weight_kg: parseFloat(form.weight_kg) || null,
        client_name: form.client_name,
        whatsapp: form.whatsapp,
        email: form.email,
        destination_state: form.destination_state,
        city: form.city,
        address: form.address,
        estimated_fee: quote.serviceFee,
        estimated_freight: quote.freightCost,
        estimated_sourcing_fee: quote.sourcingFee,
        estimated_total: quote.total,
      };
      const res = await api.submitOrder(payload);
      const waMsg = buildOrderWhatsAppMessage(res.ref, form, quote);
      const waUrl = buildWhatsAppLink(waMsg);
      setSubmitted({ ...res, waUrl, waMsg });
      // Auto-open WhatsApp in a new tab so the customer can tap Send.
      window.open(waUrl, "_blank", "noopener,noreferrer");
    } catch (e) {
      setError(e?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="container-mps py-24" data-testid="order-success">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-2xl mx-auto text-center"
        >
          <div className="w-16 h-16 mx-auto mb-8 flex items-center justify-center bg-[#C9A96E] text-[#0A0A0A]">
            <Check size={32} strokeWidth={1.5} />
          </div>
          <div className="overline text-[#C9A96E] mb-4">One Last Step</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight">
            Send your order to us on{" "}
            <span className="italic text-[#C9A96E]">WhatsApp</span>.
          </h1>
          <p className="mt-6 text-[#6B7280] leading-relaxed">
            We&apos;ve opened WhatsApp with your full order summary pre-filled.
            <br className="hidden sm:inline" />
            Just tap <span className="font-medium text-[#1A1A1A]">Send</span> and a concierge will
            confirm your quote within hours.
          </p>
          <div className="mt-8 inline-flex items-center gap-3 px-5 py-3 border border-[#C9A96E]/40 bg-[#F8F5F0]">
            <span className="overline text-[#6B7280]">Reference</span>
            <span
              data-testid="order-ref"
              className="font-medium text-[#1A1A1A] tracking-wide"
            >
              {submitted.ref}
            </span>
          </div>
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={submitted.waUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="order-success-whatsapp"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#25D366] text-[#0A0A0A] text-sm uppercase tracking-[0.18em] hover:bg-[#1EBE5C] transition-colors"
            >
              <MessageCircle size={16} /> Open WhatsApp & Send
            </a>
            <Link
              to="/"
              className="inline-flex items-center justify-center px-8 py-4 border border-[#1A1A1A]/20 text-sm uppercase tracking-[0.18em] hover:bg-[#0A0A0A] hover:text-[#F8F5F0] transition-colors"
            >
              Return Home
            </Link>
          </div>
          <p className="mt-10 text-xs text-[#6B7280] leading-relaxed max-w-md mx-auto">
            If WhatsApp didn&apos;t open automatically (browser popup blockers can
            interfere), tap the green button above — your order details are still
            pre-filled and ready to send.
          </p>
        </motion.div>
      </section>
    );
  }

  return (
    <section className="container-mps py-20 md:py-28" data-testid="order-form">
      <div className="max-w-3xl mx-auto">
        <div className="overline text-[#C9A96E] mb-3">Order Enquiry</div>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight">
          Place an order with <span className="italic text-[#C9A96E]">My Private Shopper</span>.
        </h1>

        {/* Progress */}
        <div className="mt-12 flex items-center gap-4">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-4 flex-1">
              <div
                data-testid={`step-indicator-${i}`}
                className={`w-8 h-8 flex items-center justify-center text-xs font-medium border ${
                  i <= step
                    ? "bg-[#0A0A0A] text-[#C9A96E] border-[#0A0A0A]"
                    : "bg-[#F8F5F0] text-[#6B7280] border-[#E5E0D8]"
                }`}
              >
                {i + 1}
              </div>
              <div className={`overline ${i === step ? "text-[#1A1A1A]" : "text-[#6B7280]"}`}>
                {label}
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-px ${i < step ? "bg-[#C9A96E]" : "bg-[#E5E0D8]"}`} />
              )}
            </div>
          ))}
        </div>

        <div className="mt-14 bg-[#F8F5F0] border border-[#E5E0D8] p-8 md:p-12">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.35 }}
            >
              {step === 0 && (
                <div className="space-y-6">
                  <Field label="Product URL (optional)">
                    <input
                      data-testid="form-product-url"
                      type="url"
                      placeholder="https://www.selfridges.com/..."
                      value={form.product_url}
                      onChange={(e) => update("product_url", e.target.value)}
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Product Description (required if no URL)">
                    <textarea
                      data-testid="form-product-description"
                      rows={3}
                      placeholder="Describe the item, brand, size, colour..."
                      value={form.product_description}
                      onChange={(e) => update("product_description", e.target.value)}
                      className={inputCls}
                    />
                  </Field>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Field label="Estimated Product Value (£)">
                      <input
                        data-testid="form-product-value"
                        type="number"
                        min="0"
                        placeholder="150"
                        value={form.product_value_gbp}
                        onChange={(e) => update("product_value_gbp", e.target.value)}
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Estimated Weight (kg, optional)">
                      <input
                        data-testid="form-weight"
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="3"
                        value={form.weight_kg}
                        onChange={(e) => update("weight_kg", e.target.value)}
                        className={inputCls}
                      />
                    </Field>
                  </div>
                  <Field label="Fixed-price item (optional)">
                    <select
                      data-testid="form-fixed-item"
                      value={form.fixed_price_item}
                      onChange={(e) => update("fixed_price_item", e.target.value)}
                      className={inputCls}
                    >
                      <option value="">— None (use weight rates) —</option>
                      {FIXED_PRICE_KEYS.map((k) => (
                        <option key={k} value={k}>{k}</option>
                      ))}
                    </select>
                  </Field>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      data-testid="form-complex-sourcing"
                      type="checkbox"
                      checked={form.complex_sourcing}
                      onChange={(e) => update("complex_sourcing", e.target.checked)}
                      className="mt-1 h-4 w-4 accent-[#C9A96E]"
                    />
                    <span className="text-sm text-[#1A1A1A]">
                      I need MPS to source this — I don&apos;t have a link
                      <span className="block text-xs text-[#6B7280] mt-1">
                        Adds £30 complex sourcing fee.
                      </span>
                    </span>
                  </label>
                  <Field label="Special Instructions (optional)">
                    <textarea
                      data-testid="form-instructions"
                      rows={2}
                      placeholder="Anything else we should know?"
                      value={form.special_instructions}
                      onChange={(e) => update("special_instructions", e.target.value)}
                      className={inputCls}
                    />
                  </Field>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Field label="Full Name">
                      <input
                        data-testid="form-name"
                        value={form.client_name}
                        onChange={(e) => update("client_name", e.target.value)}
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Email">
                      <input
                        data-testid="form-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => update("email", e.target.value)}
                        className={inputCls}
                      />
                    </Field>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <Field label="WhatsApp Number">
                      <input
                        data-testid="form-whatsapp"
                        type="tel"
                        placeholder="+234..."
                        value={form.whatsapp}
                        onChange={(e) => update("whatsapp", e.target.value)}
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Destination State">
                      <select
                        data-testid="form-state"
                        value={form.destination_state}
                        onChange={(e) => update("destination_state", e.target.value)}
                        className={inputCls}
                      >
                        {NIGERIAN_STATES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <Field label="City / Town">
                    <input
                      data-testid="form-city"
                      value={form.city}
                      onChange={(e) => update("city", e.target.value)}
                      className={inputCls}
                    />
                  </Field>
                  <Field label="Full Delivery Address">
                    <textarea
                      data-testid="form-address"
                      rows={3}
                      value={form.address}
                      onChange={(e) => update("address", e.target.value)}
                      className={inputCls}
                    />
                  </Field>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-8">
                  <Summary form={form} quote={quote} />
                  <div className="bg-[#0A0A0A] text-[#F8F5F0] p-6 md:p-8">
                    <div className="overline text-[#C9A96E] mb-3">Estimated Total</div>
                    <div className="font-serif text-5xl text-[#C9A96E]" data-testid="form-total">
                      {formatGBP(quote.total)}
                    </div>
                    <p className="mt-3 text-xs text-[#F8F5F0]/60">
                      Final pricing confirmed by your concierge before any payment is made.
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {error && (
            <div
              data-testid="form-error"
              className="mt-6 px-4 py-3 bg-[#0A0A0A] text-[#F8F5F0] text-sm border-l-2 border-[#C9A96E]"
            >
              {String(error)}
            </div>
          )}

          <div className="mt-10 flex flex-col-reverse sm:flex-row sm:justify-between gap-4">
            <button
              data-testid="form-back-btn"
              onClick={prev}
              disabled={step === 0 || submitting}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#E5E0D8] text-sm uppercase tracking-[0.16em] disabled:opacity-40 hover:bg-[#E5E0D8]"
            >
              <ChevronLeft size={16} /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button
                data-testid="form-next-btn"
                onClick={next}
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-[#0A0A0A] text-[#F8F5F0] text-sm uppercase tracking-[0.16em] hover:bg-[#1A1A1A]"
              >
                Next <ChevronRight size={16} />
              </button>
            ) : (
              <button
                data-testid="form-submit-btn"
                onClick={submit}
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-[#C9A96E] text-[#0A0A0A] text-sm uppercase tracking-[0.16em] hover:bg-[#A68A52] disabled:opacity-60"
              >
                {submitting ? <Loader2 className="animate-spin" size={16} /> : null}
                {submitting ? "Submitting..." : "Submit Order"}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline text-[#1A1A1A] mb-2 block">{label}</span>
      {children}
    </label>
  );
}

function SummaryRow({ k, v }) {
  return (
    <div className="flex justify-between gap-6 py-2 border-b border-[#E5E0D8] last:border-b-0">
      <div className="overline text-[#6B7280]">{k}</div>
      <div className="text-sm text-[#1A1A1A] text-right max-w-[60%] break-words">
        {v || "—"}
      </div>
    </div>
  );
}

function Summary({ form, quote }) {
  return (
    <div className="space-y-1 text-sm">
      <SummaryRow k="Name" v={form.client_name} />
      <SummaryRow k="Email" v={form.email} />
      <SummaryRow k="WhatsApp" v={form.whatsapp} />
      <SummaryRow k="Product URL" v={form.product_url} />
      <SummaryRow k="Description" v={form.product_description} />
      <SummaryRow k="Value" v={form.product_value_gbp ? `£${form.product_value_gbp}` : ""} />
      <SummaryRow k="Fixed Price Item" v={form.fixed_price_item} />
      <SummaryRow k="Weight" v={form.weight_kg ? `${form.weight_kg} kg` : ""} />
      <SummaryRow k="Sourcing Add-on" v={form.complex_sourcing ? "Yes (+£30)" : "No"} />
      <SummaryRow k="State" v={form.destination_state} />
      <SummaryRow k="City" v={form.city} />
      <SummaryRow k="Address" v={form.address} />
      <SummaryRow k="Instructions" v={form.special_instructions} />
      <div className="pt-3 mt-3 border-t border-[#E5E0D8]">
        <SummaryRow k="Service Fee" v={formatGBP(quote.serviceFee)} />
        <SummaryRow k="Freight" v={formatGBP(quote.freightCost)} />
        <SummaryRow k="Sourcing Fee" v={formatGBP(quote.sourcingFee)} />
      </div>
    </div>
  );
}
